import { Component, NgZone, OnInit } from '@angular/core';
import { HttpService } from 'app/services/http.service';
import { ConfirmationService, MessageService } from 'primeng/api';

/**
 * Botón de Pánico — lo contratado, visto desde adentro.
 *
 * ESTA PANTALLA NO ADMINISTRA LA OPERACIÓN. El alta del servicio, las
 * propiedades, los titulares y las alertas los administra cada cliente en
 * clientes.controlguard.cl; el refactor del 2026-08-24 movió eso al autoservicio
 * y lo dejó ahí a propósito. Acá se mira lo que ninguna de esas pantallas puede
 * mostrar: TODAS las empresas juntas, qué instalaciones tienen el botón
 * contratado y cuánto suma.
 *
 * LA UNIDAD ES LA INSTALACIÓN, no el cliente. Agrosuper puede tener el botón en
 * la planta San Fernando y no en Coquimbo; un condominio, en la Puerta Norte y no
 * en la Sur. La versión anterior de esta pantalla se quedó en el modelo viejo
 * —elegía un CLIENTE y le mandaba ese id a rutas que buscan por instalación— y
 * daba 404 en todo; se reescribió entera, no se parchó.
 *
 * Lo que SÍ se hace desde acá es lo que el cliente no puede tocar: ROTAR LOS
 * CÓDIGOS de difusión. Deja a la instalación sin recibir la alarma hasta que cada
 * teléfono vuelva a hablar con el servidor, así que pide confirmación y dice esa
 * consecuencia con todas sus letras.
 *
 * Lo que deliberadamente NO tiene control acá, porque el backend tampoco lo
 * acepta y ofrecer un botón que no funciona es peor que no tenerlo:
 *
 *   - El TRAMO contratado. Cambiarlo es cambiarle el precio a un contrato
 *     firmado; se solicita, no se edita, y el backend responde 409.
 *   - `Activo`. El PUT ignora ese campo aunque venga en el cuerpo. Apagar una
 *     instalación es soporte sobre la base; dar de baja el servicio es el DELETE,
 *     que además cierra el contrato.
 *
 * Cuánto se ve depende de la sesión: el staff de Control Guard ve todas las
 * empresas (TipoUsuario SUPER); cualquier otro interno, solo la suya. Lo decide el
 * backend y acá no vive una segunda copia de esa regla.
 */
@Component({
  selector: 'app-botonpanico',
  templateUrl: './botonpanico.component.html',
  styleUrls: ['./botonpanico.component.scss']
})
export class BotonpanicoComponent implements OnInit {

  cargando: boolean = false;

  configuraciones: any[] = [];
  promocionHoy: any = null;

  // Empresas del sistema, para el filtro de arriba.
  empresas: any[] = [];
  empresaSeleccionada: string = '';

  // Fila abierta en la tabla: lo que no cabe en una columna vive ahí abajo.
  filasAbiertas: any = {};

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private http: HttpService,
    private ngZone: NgZone,
  ) { }

  async ngOnInit(): Promise<void> {
    await this.getEmpresas();
    await this.carga();
  }

  async getEmpresas(): Promise<void> {
    const respuesta = await this.http.getEmpresaService();
    this.empresas = (respuesta && respuesta.empresas) ? respuesta.empresas : (respuesta || []);
    this.ngZone.run(() => { });
  }

  async carga(): Promise<void> {
    this.cargando = true;

    const respuesta = await this.http.getConfiguracionesPanicoService(this.empresaSeleccionada);
    this.configuraciones = (respuesta && respuesta.Configuraciones) ? respuesta.Configuraciones : [];
    this.promocionHoy = (respuesta && respuesta.PromocionHoy) ? respuesta.PromocionHoy : null;

    this.cargando = false;
    // El target de este proyecto es esnext, así que lo que sigue a un await queda
    // fuera de la zona de Angular y la vista no se entera de que cambiaron los
    // datos. Ver el resto de las pantallas: todas terminan igual.
    this.ngZone.run(() => { });
  }

  // El filtro se aplica en el SERVIDOR: traer las 151 empresas para esconder casi
  // todas no tiene sentido, y además el backend ya sabe qué puede mostrar.
  async cambiaEmpresa(): Promise<void> {
    await this.carga();
  }

  /**
   * Chrome ofrece las direcciones que tiene guardadas encima del filtro del
   * dropdown: lo lee como un campo de correo y tapa las opciones reales. El
   * autocomplete="off" que trae PrimeNG en ese input no alcanza —Chrome lo
   * ignora para el autocompletado de direcciones—, pero sí respeta
   * "new-password". El input solo existe mientras el panel está abierto, así
   * que hay que marcarlo cada vez que se muestra.
   */
  apagaAutocompletado(): void {
    const filtro = document.querySelector('.p-dropdown-panel .p-dropdown-filter') as HTMLInputElement;
    if (!filtro) return;

    filtro.setAttribute('autocomplete', 'new-password');
    // Gestores de contraseñas de terceros miran sus propios atributos.
    filtro.setAttribute('data-lpignore', 'true');
    filtro.setAttribute('data-1p-ignore', 'true');
  }

  // ------------------------------------------------------------- totales

  get activas(): number {
    return this.configuraciones.filter(c => c.Activo).length;
  }

  get propiedadesUsadas(): number {
    return this.configuraciones.reduce((a, c) => a + (c.PropiedadesUsadas || 0), 0);
  }

  get propiedadesContratadas(): number {
    return this.configuraciones.reduce((a, c) => a + (c.LicenciaPropiedades || 0), 0);
  }

  /**
   * Lo que suman los contratos vigentes al mes, en pesos NETOS.
   *
   * El valor sale del CONTRATO y no del catálogo de tramos: el precio se copia al
   * firmar, así que quien entró antes de un alza conserva el suyo y el total es
   * una suma de precios distintos. Una instalación sin contrato —las que nacieron
   * antes de los tramos— suma cero en vez de la tarifa de hoy: inventarle un
   * precio sería peor que mostrar que le falta.
   */
  get pesosMensual(): number {
    return this.configuraciones.reduce((a, c) => a + this.valorMensual(c), 0);
  }

  // Lo que hoy todavía no se cobra: entra al total cuando termine su promoción.
  get pesosEnPromocion(): number {
    return this.configuraciones.filter(c => c.EnPromocion).reduce((a, c) => a + this.valorMensual(c), 0);
  }

  get pesosFacturable(): number {
    return this.pesosMensual - this.pesosEnPromocion;
  }

  get enPromocion(): number {
    return this.configuraciones.filter(c => c.EnPromocion).length;
  }

  get sinContrato(): number {
    return this.configuraciones.filter(c => !c.ContratoVigente).length;
  }

  // ------------------------------------------------------------- por fila

  valorMensual(c: any): number {
    return (c && c.ContratoVigente && c.ContratoVigente.Tramo)
      ? (c.ContratoVigente.Tramo.ValorMensual || 0)
      : 0;
  }

  nombreTramo(c: any): string {
    if (!c || !c.ContratoVigente || !c.ContratoVigente.Tramo) return '';
    const t = c.ContratoVigente.Tramo;
    return t.Nombre || t.Codigo || '';
  }

  // Ya cargó más propiedades de las que le alcanza la licencia. No debería poder
  // pasar —el alta lo corta— pero si pasa hay que verlo acá, y no enterarse
  // cuando el cliente reclame que no puede agregar una más.
  cupoExcedido(c: any): boolean {
    return !!c && (c.PropiedadesUsadas || 0) > (c.LicenciaPropiedades || 0);
  }

  pesos(valor: number): string {
    return '$' + (valor || 0).toLocaleString('es-CL');
  }

  // --------------------------------------------------------- rotar códigos

  rotaCodigos(fila: any): void {
    const nombre = (fila.Instalacion && fila.Instalacion.Nombre) || 'esta instalación';

    this.confirmationService.confirm({
      message: 'Los códigos de difusión de ' + nombre + ' cambian de inmediato. '
        + 'Hasta que cada teléfono vuelva a hablar con el servidor, esa instalación NO recibe la alarma. '
        + 'Solo se hace si hay sospecha de que los códigos se filtraron. ¿Rotarlos?',
      header: 'Rotar los códigos de difusión',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, rotar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.http.rotarCodigosPanicoService(fila.Instalacion._id).subscribe(async () => {
          this.messageService.add({ severity: 'success', summary: 'Códigos rotados', detail: 'Los teléfonos se reenganchan solos al volver a conectarse.' });
          await this.carga();
          this.ngZone.run(() => { });
        }, (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'No se pudieron rotar',
            detail: (error.error && error.error.error) ? error.error.error : (error.error || 'Error inesperado')
          });
          this.ngZone.run(() => { });
        });
      }
    });
  }
}
